import React, { useEffect, useState, useMemo } from 'react';
import PageLayout from 'components/PageLayout';
import LoadingState from 'components/LoadingState';
import ErrorState from 'components/ErrorState';
import {
  EmptyState,
  JudgmentStatistics,
  JudgmentFilters,
  PreSelectionSubmissionsTable,
  ActionLogPanel,
} from 'components/Judgment';
import type { StageActionLog } from 'components/Judgment/SubmissionRow';
import { useJudgmentData } from 'hooks/useJudgmentData';
import { useAuth } from 'contexts/AuthContext';
import AccessDenied from 'components/AccessDenied';
import NotAuthenticated from 'components/NotAuthenticated';
import { trackEvent } from 'services/firebase';
import { exportSMDDJudgmentToExcel } from 'services/excelExportService';
// Import test utility for debugging (only loads in development)
if (process.env.NODE_ENV === 'development') {
  import('utils/testPillarAccess');
}

const PreselectionRankingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const {
    submissions,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filterCategory,
    setFilterCategory,
    year,
    setYear,
    sortField,
    sortDirection,
    expandedRows,
    handleRefresh,
    updateSubmissionStageLocal,
    toggleRowExpansion,
    handleSort,
    categories,
    filteredAndSortedSubmissions,
    stats,
    getMainDescription,
    sanitizeAIText,
    filterStage,
    setFilterStage,
    allStages,
    // Access control
    userHasAccess,
    userPillar,
    userIsSuperAdmin,
    userIsPillarPIC,
  } = useJudgmentData();

  const actionLogs = useMemo(() => {
    const logs: StageActionLog[] = [];
    submissions.forEach(s => {
      if (s.stage_action_logs && Array.isArray(s.stage_action_logs)) {
        logs.push(...s.stage_action_logs);
      }
    });
    // Sort oldest to newest (ascending) so the Panel can reverse it to display newest first
    return logs.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [submissions]);

  useEffect(() => {
    trackEvent('page_view', {
      page_title: 'SMDD Judgment',
      page_path: '/smdd-judgment',
    });
  }, []);

  // Handle Excel export
  const handleExportToExcel = async () => {
    if (
      !filteredAndSortedSubmissions ||
      filteredAndSortedSubmissions.length === 0
    ) {
      alert('No data to export');
      return;
    }

    setIsExporting(true);

    try {
      const filename = exportSMDDJudgmentToExcel(
        filteredAndSortedSubmissions,
        `smdd-judgment-${filterCategory !== 'all' ? filterCategory.toLowerCase().replace(/\s+/g, '-') : 'all-categories'}`
      );

      // Track export event
      trackEvent('smdd_judgment_export', {
        total_submissions: filteredAndSortedSubmissions.length,
        filter_category: filterCategory,
        filename: filename,
      });
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // If not authenticated, show not authenticated message
  if (!isAuthenticated()) {
    return (
      <PageLayout
        title="Submissions"
        subtitle="Authentication required to access judgment data."
      >
        <NotAuthenticated
          message="Please log in to access the judgment page."
          buttonLink="/"
          buttonClassName="bg-red-smdd text-white hover:bg-red-smdd-dark"
          iconBgClassName="bg-yellow-100"
          iconClassName="text-yellow-600"
          className="text-center py-12"
          buttonText="Go to Home"
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Submissions"
      subtitle={
        userIsSuperAdmin
          ? 'Manage submissions from ALL pillars with super admin access and AI-powered insights.'
          : userHasAccess && userPillar
            ? `Manage submissions for ${userPillar} pillar with AI-powered insights.`
            : 'Manage submissions with AI-powered insights and comprehensive evaluation criteria.'
      }
    >
      {loading ? (
        <LoadingState message="Loading submissions for judgment..." />
      ) : error ? (
        <ErrorState
          title="Error Loading Submissions"
          error={error}
          onRetry={handleRefresh}
        />
      ) : !userHasAccess ? (
        <AccessDenied
          message="You don't have permission to view judgment data. Access is restricted to authorized personnel from specific pillars."
          iconBgClassName="bg-red-100"
          iconClassName="text-red-600"
          className="text-center py-12"
          buttonText="Return to Home"
        />
      ) : (
        <>
          <JudgmentStatistics
            stats={{
              total: stats.total,
              totalEvaluations: stats.scored,
              averageScore: stats.averageScore,
            }}
          />
          <JudgmentFilters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filterCategory={filterCategory}
            setFilterCategory={setFilterCategory}
            categories={categories}
            handleRefresh={handleRefresh}
            loading={loading}
            userPillar={userPillar}
            userIsSuperAdmin={userIsSuperAdmin}
            year={year}
            setYear={setYear}
            filterStage={filterStage}
            setFilterStage={setFilterStage}
            allStages={allStages}
          />
          {filteredAndSortedSubmissions.length === 0 ? (
            <EmptyState
              searchQuery={searchQuery}
              filterCategory={filterCategory}
            />
          ) : (
            <>
              <PreSelectionSubmissionsTable
                submissions={filteredAndSortedSubmissions}
                expandedRows={expandedRows}
                toggleRowExpansion={toggleRowExpansion}
                sortField={sortField}
                sortDirection={sortDirection}
                handleSort={handleSort}
                getMainDescription={getMainDescription}
                sanitizeAIText={sanitizeAIText}
                showStageAction={userIsSuperAdmin || userIsPillarPIC}
                onStageChange={updateSubmissionStageLocal}
                onRecalculateComplete={() => handleRefresh(true)}
              />

              {/* Export Button - Bottom Right */}
              <div className="flex justify-end mt-8 mb-6">
                <button
                  onClick={handleExportToExcel}
                  disabled={
                    isExporting || filteredAndSortedSubmissions.length === 0
                  }
                  className={`inline-flex items-center gap-3 px-8 py-3 rounded-full font-baijamjuree font-semibold text-sm uppercase tracking-wide transition-all duration-200 shadow-lg border-2 ${
                    isExporting || filteredAndSortedSubmissions.length === 0
                      ? 'bg-gray-400 text-gray-600 border-gray-300 cursor-not-allowed'
                      : 'bg-black text-white border-black hover:bg-gray-800 hover:border-gray-800 hover:shadow-xl transform hover:scale-105 active:scale-95'
                  }`}
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                        />
                      </svg>
                      Export to Excel
                    </>
                  )}
                </button>
              </div>

              {/* Action Log Panel */}
              <ActionLogPanel logs={actionLogs} />
            </>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default PreselectionRankingPage;
